import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/invitations.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class OrganizationsInvitationsEffects {
  loadOrganizationInvitations$;
  respondToOrganizationInvitation$;
  addOrInviteOrganizationUser$;
  addOrInviteOrganizationUserSuccess$;
  addOrInviteOrganizationUserFailure$;
  respondToOrganizationInvitationSuccess$;
  respondToOrganizationInvitationFailure$;
  resendOrganizationInvitation$;
  resendOrganizationInvitationSuccess$;
  resendOrganizationInvitationFailure$;
  deleteOrganizationInvitation$;
  deleteOrganizationInvitationSuccess$;
  deleteOrganizationInvitationFailure$;
  loadOrganizationInvitationById$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService) {



    this.loadOrganizationInvitations$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationInvitations),
        mergeMap(({ organizationId }) =>
          this.organizationsService.organizationsGetPendingInvitations({ organizationId }).pipe(
            map((invitations) =>
              OrganizationsActions.loadOrganizationInvitationsSuccess({ organizationId, invitations })
            ),
            catchError((error) => of(OrganizationsActions.loadOrganizationInvitationsFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.respondToOrganizationInvitation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.respondToOrganizationInvitation),
        mergeMap(({ organizationId, invitationId, action, token }) =>
          this.organizationsService.organizationsRespondInvitation({ organizationId, invitationId, body: { action, token } }).pipe(
            map((result) =>
              OrganizationsActions.respondToOrganizationInvitationSuccess({ organizationId, invitationId, status: result.status! })
            ),
            catchError((error) => of(OrganizationsActions.respondToOrganizationInvitationFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );


    this.addOrInviteOrganizationUser$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.addOrInviteOrganizationUser),
        mergeMap(({ organizationId, user, role }) =>
          this.organizationsService.organizationsAddOrInviteUser({ organizationId, body: { userId: user.id, email: user.email, role } }).pipe(
            map((result) =>
              OrganizationsActions.addOrInviteOrganizationUserSuccess({
                organizationId,
                user: result.user,
                role: role,
                invitationSent: result.invitationSent,
                organization: result.organization
                  ? { data: result.organization, loadedAt: new Date() }
                  : undefined,
                invitation: result.invitation
              })
            ),
            catchError((error) => of(OrganizationsActions.addOrInviteOrganizationUserFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );


    // Load a single invitation by ID
    this.loadOrganizationInvitationById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationInvitationById),
        mergeMap(({ organizationId, invitationId, token }) =>
          this.organizationsService.organizationsGetInvitationById({ organizationId, invitationId, token }).pipe(
            map(result => {
              if (!result.invitation) throw new Error('Invitation not found');
              return OrganizationsActions.loadOrganizationInvitationByIdSuccess({
                organizationId,
                invitationId,
                invitation: result.invitation,
                organizationName: result.organizationName
              });
            }),
            catchError(error => of(OrganizationsActions.loadOrganizationInvitationByIdFailure({ organizationId, invitationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    // Resend Invitation Effect
    this.resendOrganizationInvitation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.resendOrganizationInvitation),
        mergeMap(({ organizationId, invitationId }) =>
          this.organizationsService.organizationsResendInvitation({ organizationId, invitationId }).pipe(
            map((result) =>
              OrganizationsActions.resendOrganizationInvitationSuccess({
                organizationId,
                invitationId,
                resentAt: new Date().toISOString()
              })
            ),
            catchError((error) =>
              of(OrganizationsActions.resendOrganizationInvitationFailure({ organizationId, invitationId, error }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    // Show toast on resend success
    this.resendOrganizationInvitationSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.resendOrganizationInvitationSuccess),
        tap(() => {
          this.messageService.add({ severity: 'success', summary: 'Invitation Resent', detail: 'Invitation resent successfully.' });
        })
      ),
      { dispatch: false }
    );

    // Show toast on resend failure
    this.resendOrganizationInvitationFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.resendOrganizationInvitationFailure),
        tap(() => {
          this.messageService.add({ severity: 'error', summary: 'Resend Failed', detail: 'Failed to resend invitation.' });
        })
      ),
      { dispatch: false }
    );

    // Show success message when user is added or invited
    this.addOrInviteOrganizationUserSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.addOrInviteOrganizationUserSuccess),
        tap(({ invitationSent, user }) => {
          if (invitationSent) {
            this.messageService.add({ severity: 'success', summary: 'Invitation Sent', detail: `Invitation sent to ${user?.email || 'user'}.` });
          } else {
            this.messageService.add({ severity: 'success', summary: 'User Added', detail: `${user?.email || 'User'} added to organization.` });
          }
        })
      ),
      { dispatch: false }
    );

    // Show error message when adding or inviting user fails
    this.addOrInviteOrganizationUserFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.addOrInviteOrganizationUserFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add or invite user.' });
          this.logger.error('Add or invite organization user failed', error);
        })
      ),
      { dispatch: false }
    );


    // Show success message when invitation is responded to
    this.respondToOrganizationInvitationSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.respondToOrganizationInvitationSuccess),
        tap(({ status }) => {
          let detail = status === 'accepted'
            ? 'Invitation accepted.'
            : status === 'declined'
              ? 'Invitation declined.'
              : `Invitation ${status}.`;
          this.messageService.add({ severity: 'success', summary: 'Invitation Response', detail });
        })
      ),
      { dispatch: false }
    );

    // Show error message when responding to invitation fails
    this.respondToOrganizationInvitationFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.respondToOrganizationInvitationFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to respond to invitation.' });
          this.logger.error('Respond to organization invitation failed', error);
        })
      ),
      { dispatch: false }
    );

    // Delete Invitation Effect
    this.deleteOrganizationInvitation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.cancelOrganizationInvitation),
        mergeMap(({ organizationId, invitationId }) =>
          this.organizationsService.organizationsCancelInvitation({ organizationId, invitationId }).pipe(
            map(() =>
              OrganizationsActions.cancelOrganizationInvitationSuccess({ organizationId, invitationId })
            ),
            catchError((error) =>
              of(OrganizationsActions.cancelOrganizationInvitationFailure({ organizationId, invitationId, error }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    // Show toast on delete success
    this.deleteOrganizationInvitationSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.cancelOrganizationInvitationSuccess),
        tap(() => {
          this.messageService.add({ severity: 'success', summary: 'Invitation Deleted', detail: 'Invitation deleted successfully.' });
        })
      ),
      { dispatch: false }
    );

    // Show toast on delete failure
    this.deleteOrganizationInvitationFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.cancelOrganizationInvitationFailure),
        tap(() => {
          this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Failed to delete invitation.' });
        })
      ),
      { dispatch: false }
    );
  }
}